import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/hooks/use-theme';
import { IconMoon, IconSun } from './sidebar-icons';
import { BrandIdentity } from '@/components/branding/BrandIdentity';
import { usePlatformBranding } from '@/hooks/use-platform-branding';
import styles from './PublicShell.module.scss';

interface PublicShellProps {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function PublicShell({ title, description, children, footer }: PublicShellProps) {
  const { theme, toggle } = useTheme();
  const { branding } = usePlatformBranding();
  const ThemeIcon = theme === 'dark' ? IconSun : IconMoon;
  const themeLabel = theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro';

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <Link to="/login" className={styles.brand} aria-label={`${branding.app_name} — entrar`}>
          <BrandIdentity />
        </Link>
        <button type="button" className={styles.themeButton} onClick={toggle} aria-label={themeLabel} title={themeLabel}>
          <ThemeIcon />
        </button>
      </header>

      <main className={styles.main}>
        <section className={styles.intro} aria-label={`Sobre o ${branding.app_name}`}>
          <p className={styles.statement}>Conversa, contexto e automação no mesmo fluxo.</p>
          <p>
            Acompanhe o atendimento, organize contatos e pipelines e coordene campanhas sem perder o contexto das conversas.
          </p>
          <div className={styles.flow} aria-label="Fluxo do produto">
            <span>Conversas</span>
            <i aria-hidden="true" />
            <span>Contatos</span>
            <i aria-hidden="true" />
            <span>Pipeline</span>
            <i aria-hidden="true" />
            <span>Automação</span>
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.heading}>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
          {children}
          {footer && <div className={styles.footer}>{footer}</div>}
        </section>
      </main>
    </div>
  );
}
